<template>
  <div>
    <b-row align-h="between" class="mx-3">
      <div class="my-1 col-md-3">
        Showing {{ from }} to {{ to }} of {{ totalItems }} observations
      </div>

      <div class="col-md-3 my-1">
        <b-pagination
          :total-rows="totalItems"
          :per-page="perPage"
          :value="currentPage"
          @input="setPage"
          class="my-0 pagination-holder"
          prev-text="Prev"
          next-text="Next"
          align="fill"
        />
      </div>

      <div class="col-md-3 my-1">
        <b-form-group
          label="Per Page"
          label-align="right"
          label-cols-sm="7"
          label-cols-md="8"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            :value="perPage"
            @change="setPerPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
            align="right"
          ></b-form-select>
        </b-form-group>
      </div>
    </b-row>
    <b-table
      show-empty
      stacked="md"
      id="observationsTable"
      ref="observationsTable"
      class="border-bottom"
      :busy="isLoading"
      :items="observationsProvider"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
    >
      <template #head(observedAt)>
        <span id="location-observations-start-header" class="d-inline-flex align-items-center">
          Observation Date
          <i class="fa fa-question-circle ml-1 text-muted"></i>
        </span>
        <b-tooltip target="location-observations-start-header" placement="top" triggers="hover focus">
          Start date of the observation
        </b-tooltip>
      </template>
      <template #head(createdAt)>
        <span id="location-observations-created-header" class="d-inline-flex align-items-center">
          Created Date
          <i class="fa fa-question-circle ml-1 text-muted"></i>
        </span>
        <b-tooltip target="location-observations-created-header" placement="top" triggers="hover focus">
          When the observation was submitted to CitSci
        </b-tooltip>
      </template>
      <template v-slot:cell(createdAt)="row">
        <DateTime :date="row.item.createdAt" :dateTime="false" />
      </template>
      <template v-slot:cell(options)="row">
        <div>
          <b-btn
            class="mt-1 mr-1"
            :to="{ name: 'ObservationShow', params: { id: row.item.id } }"
            alt="View Observation"
            title="View Observation"
          >
            <font-awesome-icon icon="eye" /> View
          </b-btn>
          <b-btn
            v-if="
              row.item.project.id &&
              membershipMap[row.item.project.id] &&
              membershipMap[row.item.project.id].isManager
            "
            class="mt-1"
            @click="onClickDelete(row.item)"
            alt="Delete this Observation from the Project"
            title="Delete this Observation from the Project"
          >
            <i class="fa fa-trash"></i> Delete
          </b-btn>
        </div>
      </template>
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Observations..." />
      </template>
    </b-table>
    <b-row align-h="between" class="mx-3">
      <div class="my-1 col-md-3">
        Showing {{ from }} to {{ to }} of {{ totalItems }} observations
      </div>

      <div class="col-md-3 my-1">
        <b-pagination
          :total-rows="totalItems"
          :per-page="perPage"
          :value="currentPage"
          @input="setPage"
          class="my-0 pagination-holder"
          prev-text="Prev"
          next-text="Next"
          align="fill"
        />
      </div>

      <div class="col-md-3 my-1">
        <b-form-group
          label="Per Page"
          label-align="right"
          label-cols-sm="7"
          label-cols-md="8"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            :value="perPage"
            @change="setPerPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
            align="right"
          ></b-form-select>
        </b-form-group>
      </div>
    </b-row>
    <b-modal id="deleteObservationModal" title="Delete Observation" centered>
      <div v-if="selectedObservation">
        <p>
          Do you want to remove this observation from the project? This cannot
          be undone.
        </p>
        <div>
          <span class="font-weight-bold">Observed by: </span>
          {{ selectedObservation.user.displayName }}
        </div>
        <div>
          <span class="font-weight-bold">Using Datasheet: </span>
          {{ selectedObservation.datasheet.name }}
        </div>
        <div>
          <span class="font-weight-bold">Observed at: </span>
          {{ displayDate(selectedObservation.observedAt) }}
        </div>
      </div>
      <template slot="modal-footer">
        <div>
          <b-btn variant="danger" @click="onConfirmDelete()">Yes, delete</b-btn>
          <b-btn @click="closeModal">No</b-btn>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import VueLoader from "../layout/Loader";
import DateTime from "../common/date-time/DateTime.vue";

export default {
  name: "ObservationsTable",
  components: {
    VueLoader,
    DateTime,
  },
  data() {
    return {
      pageOptions: [10, 25, 50, 100],
      showModal: null,
      selectedObservation: null,
      searchText: "",
      sortBy: "observedAt",
      sortDesc: true,
      sortDirection: "desc",
      tableFields: [
        {
          key: "observedAt",
          label: "Observation Date",
          sortable: true,
          tdClass: "text-dark",
          formatter: (val) => {
            return this.displayDate(val);
          },
        },
        {
          key: "createdAt",
          label: "Created Date",
          sortable: true,
          tdClass: "text-dark",
          // formatter: (val) => {
          //   return this.displayDate(val);
          // },
        },
        {
          key: "datasheet.name",
          label: "Datasheet",
          sortable: true,
          tdClass: "text-dark",
          formatter: (val) => {
            return val;
          },
        },
        {
          key: "user.displayName",
          label: "Observed By",
          sortable: false,
          formatter: (val, fieldName, item) => {
            return item && item.user && item.user.displayName ;
          },
        },
        {
          key: "options",
          label: "Options",
          sortable: false,
          class: "text-right py-0",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      membershipMap: "user/profile/membershipMap",
      location: "location/details",
      observations: "location/observations",
      isLoading: "location/observationsIsLoading",
      totalItems: "location/observationsTotal",
      currentPage: "location/observationsPage",
      perPage: "location/observationsPerPage",
      from: "location/observationsFrom",
      to: "location/observationsTo",
      error: "location/observationsError",
    }),
  },
  methods: {
    ...mapActions({
      fetchObservations: "location/fetchObservations",
      deleteObservation: "observation/show/deleteObservation",
    }),
    async observationsProvider(ctx, callback) {
      if (!this.location) {
        return [];
      }

      // let urlParams = `${this.urlBuilder(ctx)}`;
      await this.fetchObservations(ctx);
      return this.observations;
    },
    ...mapMutations({
      setPage: "location/LOCATION_SET_OBSERVATIONS_PAGE",
      setPerPage: "location/LOCATION_SET_OBSERVATIONS_PER_PAGE",
    }),

    closeModal() {
      this.$bvModal.hide("deleteObservationModal");
      this.selectedObservation = null;
    },
    onClickDelete(observation) {
      this.selectedObservation = { ...observation };
      this.$bvModal.show("deleteObservationModal");
    },
    onConfirmDelete() {
      this.deleteObservation({
        id: this.selectedObservation.id,
      }).then(() => {
        this.$refs.observationsTable.refresh();
      });
      this.closeModal();
    },
  },
};
</script>

<style></style>
